import { EventType, EventExample } from "./progressData";
import { useState, useEffect } from "react";
import EventSelector from "./EventSelector";
import { useUser } from "@/queries/user/useUser";
import { useTrackerEvents } from "@/queries/tracker/useTrackerEvents";
import { TrackerEvent } from "@/api/SolarCloudApi";
import Events from "./Events";

const eventTypes = Object.values(EventType);

export function mapTrackerEventToEventExample(event: TrackerEvent): EventExample {
  let type = EventType.INFORMATION;

  if (event.category) {
    const category = event.category.toLowerCase();
    if (category === "disbursement") {
      type = EventType.DISBURSEMENT;
    } else if (category === "reminder") {
      type = EventType.REMINDERS;
    } else if (category === "updatesinformation") {
      type = EventType.INFORMATION;
    } else if (category === "actionrequirement") {
      type = EventType.ACTIONS;
    } else if (category === "purchase" || category === "deposit" || category === "balance" || category === "payment") {
      type = EventType.PAYMENTS;
    }
  } else {
    // Fallback to TrackerEventType if category is absent
    if (event.type) {
      const et = event.type.toLowerCase();
      if (et === "payout" || et === "refundorder" || et === "refundpanel") {
        type = EventType.DISBURSEMENT;
      } else if (et === "order" || et === "payment") {
        type = EventType.PAYMENTS;
      } else if (et === "agreements" || et === "documents" || et === "userallocations" || et === "organisationallocations") {
        type = EventType.ACTIONS;
      }
    }
  }

  let update = event.label || "";
  if (event.amount !== undefined && event.amount !== null && event.amount > 0) {
    const currencySymbol = event.currency === "USD" ? "$" : (event.currency === "AUD" || !event.currency ? "$" : event.currency);
    update = `${currencySymbol}${event.amount.toFixed(2)}`;
  }

  let title = event.title || "Event Update";
  let description = event.label || "";

  if (title === "Bank Disbursement") {
    title = "Disbursement";
  }

  let payload = event.payload;
  if (payload && payload.deviceContext && payload.deviceContext.ipAddress) {
    const ip = payload.deviceContext.ipAddress;
    const parts = ip.split(".");
    if (parts.length === 4) {
      payload = {
        ...payload,
        deviceContext: {
          ...payload.deviceContext,
          ipAddress: `${parts[0]}.${parts[1]}.${parts[2]}.xxx`
        }
      };
    }
  }

  return {
    type,
    timestamp: event.timestamp || event.updated || new Date().toISOString(),
    title,
    description,
    priority: event.priority,
    update,
    messages: event.messages || [],
    uuid: event.uuid,
    entityUuid: event.entityUuid,
    payload,
    manualPaymentRequestUuid: event.manualPaymentRequestUuid,
    manualPaymentRequestState: event.manualPaymentRequestState,
    paymentMethod: event.paymentMethod,
    label: event.label,
  };
}

export default function Progress() {
  const { data: user } = useUser();
  const { data: trackerEvents } = useTrackerEvents({
    queryConfig: {
      enabled: !!user,
    },
  });
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [showAllMode, setShowAllMode] = useState(false);
  const [globalExpandTrigger, setGlobalExpandTrigger] = useState<{ action: "expand" | "collapse", id: number } | null>(null);

  useEffect(() => {
    setVisibleCount(5);
    setShowAllMode(false);
  }, [selectedEventType, selectedDate]);

  useEffect(() => {
    setGlobalExpandTrigger({
      action: isAllExpanded ? "expand" : "collapse",
      id: Date.now()
    });
  }, [isAllExpanded]);

  if (!trackerEvents) return null;

  const hasLiveEvents = trackerEvents && trackerEvents.length > 0;
  const rawEvents = hasLiveEvents
    ? trackerEvents.map(mapTrackerEventToEventExample)
    : [];

  const filteredEvents = rawEvents.filter((event: EventExample) => {
    // if date selected, filter before date
    if (selectedDate && new Date(event.timestamp) < selectedDate) return false;
    if (selectedEventType === EventType.ALL || !selectedEventType) return true;
    return event.type === selectedEventType;
  });

  const eventsToShow = showAllMode ? filteredEvents.length : visibleCount;
  const visibleEvents = filteredEvents.slice(0, eventsToShow);
  const hasMore = !showAllMode && filteredEvents.length > visibleCount;
  const allEventsVisible = visibleCount >= filteredEvents.length;

  const handleExpandCollapseAll = () => {
    setIsAllExpanded(!isAllExpanded);
  };

  const handleShowAll = () => {
    setShowAllMode(true);
  };

  return (
    <>
      <div className="flex justify-start mb-8 gap-4 pl-[144px]">
        <EventSelector
          eventTypes={eventTypes}
          selectedEvent={selectedEventType}
          setSelectedEvent={setSelectedEventType}
        />
        {/* <div className="flex items-center justify-between w-[140px] px-4 py-1 h-8 rounded-full bg-[#24262e] text-brand-orange-500 text-xs shadow-md cursor-pointer hover:bg-white/5 transition-colors">
          <span>time frame</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isAllExpanded ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div> */}
      </div>

      {visibleEvents.length === 0 ? (
        <div className="pl-[144px] py-12 text-brand-neutral-400">
          <p className="text-sm italic">No recent tracker updates found.</p>
        </div>
      ) : (
        <Events events={visibleEvents} globalExpandTrigger={globalExpandTrigger} />
      )}

      <div className="flex flex-col items-start mt-4 space-y-3 pl-[144px] w-full max-w-4xl">
        <button
          onClick={handleExpandCollapseAll}
          className="flex items-center gap-2 text-xs text-brand-neutral-400 hover:text-white transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isAllExpanded ? "rotate-180" : ""}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
          {isAllExpanded ? "collapse all" : "expand all"}
        </button>
      </div>

      {/* Pagination / Show All */}
      {visibleEvents.length > 0 && (
        <div className="flex justify-start pl-[144px] mt-6 pb-20">
          {!showAllMode && !allEventsVisible && (
            <button
              onClick={handleShowAll}
              className="flex items-center gap-1 bg-brand-orange-500 text-black px-6 py-1.5 rounded-full font-medium text-sm hover:bg-brand-orange-600 transition-colors mr-3"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              show all
            </button>
          )}
          {hasMore && (
            <button
              onClick={() => setVisibleCount((prev) => prev + 5)}
              className="flex items-center gap-1 bg-brand-orange-500 text-black px-6 py-1.5 rounded-full font-medium text-sm hover:bg-brand-orange-600 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              more
            </button>
          )}
        </div>
      )}
    </>
  );
}
